// DEPLOY: /var/www/woody.wtf/frontend/src/pages/AiPlane.js
// AI Plane: admin-only AI assistant with codebase access.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const s = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  nav: {
    background: 'var(--surface)', borderBottom: '1px solid var(--border)',
    padding: '0 32px', height: '56px', display: 'flex',
    alignItems: 'center', justifyContent: 'space-between',
  },
  logo: {
    fontFamily: "'JetBrains Mono', monospace", fontSize: '13px', fontWeight: 500,
    letterSpacing: '0.1em', color: 'var(--accent)', textTransform: 'uppercase',
  },
  navRight: { display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' },
  navBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)',
    fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif",
    padding: '6px 12px', borderRadius: 'var(--radius)',
  },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '24px 32px' },
  heading: { fontSize: '24px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '20px' },
  columns: { display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' },
  side: { flex: '1 1 280px', minWidth: '260px' },
  main: { flex: '3 1 440px', minWidth: '300px' },
  section: {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'calc(var(--radius) * 2)', padding: '20px', marginBottom: '20px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, marginBottom: '14px' },
  btn: {
    padding: '5px 12px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: 500, fontFamily: "'Space Grotesk', sans-serif",
  },
  row: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  input: {
    padding: '7px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    fontSize: '13px', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--bg)',
    color: 'var(--text)', outline: 'none',
  },
  listItem: {
    padding: '9px 11px', borderRadius: 'var(--radius)', cursor: 'pointer',
    fontSize: '13px', marginBottom: '6px', border: '1px solid transparent',
  },
  listItemActive: { background: 'var(--accent-dim)', border: '1px solid var(--accent)', color: 'var(--accent)' },
  muted: { color: 'var(--text-muted)', fontSize: '12px' },
  mono: { fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' },
  messages: {
    maxHeight: '52vh', overflowY: 'auto', display: 'flex',
    flexDirection: 'column', gap: '12px', marginBottom: '14px',
  },
  bubble: {
    padding: '10px 13px', borderRadius: 'var(--radius)', fontSize: '14px',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '92%',
  },
  tool: {
    alignSelf: 'flex-start', maxWidth: '92%', padding: '6px 10px',
    borderRadius: 'var(--radius)', background: 'var(--bg)',
    border: '1px dashed var(--border)', cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace", fontSize: '11.5px', color: 'var(--text-muted)',
  },
  pre: {
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
    padding: '12px', overflowX: 'auto', fontSize: '11.5px', maxHeight: '45vh',
    fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'pre',
  },
  commitRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '10px', padding: '7px 0', borderBottom: '1px solid var(--border)',
  },
  err: { color: 'var(--error)', fontSize: '13px', marginBottom: '10px' },
  ok: { color: 'var(--success)', fontSize: '13px', marginBottom: '10px' },
};

function Btn({ onClick, color = 'var(--accent)', textColor = 'var(--on-accent)', children, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...s.btn, background: color, color: textColor, opacity: disabled ? 0.5 : 1 }}
    >
      {children}
    </button>
  );
}

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

const BLANK_PROJECT = { name: '', repo_path: '', service_name: '', build_command: '', test_command: '', description: '', instructions: '' };

// ---------------------------------------------------------------------------
// Tool message - collapsed by default, since results can be whole files
// ---------------------------------------------------------------------------
function ToolMessage({ m }) {
  const [open, setOpen] = useState(false);
  const label = m.meta?.tool || 'tool';
  const args = m.meta?.args && Object.keys(m.meta.args).length
    ? ' ' + Object.entries(m.meta.args).map(([k, v]) => `${k}=${v}`).join(' ')
    : '';
  const failed = typeof m.content === 'string' && m.content.startsWith('Error:');

  return (
    <div style={s.tool} onClick={() => setOpen(o => !o)}>
      <span style={{ color: failed ? 'var(--error)' : 'var(--accent)' }}>▸ {label}</span>
      {args}
      {open && <pre style={{ ...s.pre, marginTop: '8px', maxHeight: '30vh' }}>{m.content}</pre>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copy button for assistant messages
// ---------------------------------------------------------------------------
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <span
      style={{ ...s.muted, cursor: 'pointer' }}
      title="Copy to clipboard"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
export default function AiPlane() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convId, setConvId] = useState(null);
  const [conv, setConv] = useState(null);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('');
  const [git, setGit] = useState(null);
  const [diff, setDiff] = useState(null);
  const [commits, setCommits] = useState(null);
  const [index, setIndex] = useState(null);
  const [commitMsg, setCommitMsg] = useState('');
  const [newProject, setNewProject] = useState(BLANK_PROJECT);
  const [showAddProject, setShowAddProject] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [taskError, setTaskError] = useState('');

  const pollRef = useRef(null);
  const endRef = useRef(null);

  const flash = (m, isErr = false) => {
    if (isErr) { setError(m); setMsg(''); } else { setMsg(m); setError(''); }
    setTimeout(() => { setMsg(''); setError(''); }, 4000);
  };

  const fail = (e, fallback) => flash(e.response?.data?.error || fallback, true);

  useEffect(() => {
    if (!user?.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Stop polling if the page unmounts mid-task.
  useEffect(() => () => clearInterval(pollRef.current), []);

  // -------------------------------------------------------------------------
  // Loaders
  // -------------------------------------------------------------------------
  const loadProjects = useCallback(async () => {
    try {
      const r = await api.get('/ai/projects');
      setProjects(r.data.projects);
    } catch {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversations = useCallback(async (pid) => {
    try {
      const r = await api.get('/ai/conversations', { params: pid ? { project_id: pid } : {} });
      setConversations(r.data.conversations);
    } catch {
      setError('Failed to load conversations');
    }
  }, []);

  const loadConversation = useCallback(async (cid) => {
    try {
      const r = await api.get('/ai/conversation', { params: { conversation_id: cid } });
      setConv(r.data);
    } catch {
      setError('Failed to load conversation');
    }
  }, []);

  const loadGit = useCallback(async (pid) => {
    setDiff(null);
    try {
      const r = await api.get('/ai/git/status', { params: { project_id: pid } });
      setGit(r.data);
    } catch (e) {
      setGit({ error: e.response?.data?.error || 'git status failed' });
    }
  }, []);

  const loadCommits = useCallback(async (pid) => {
    try {
      const r = await api.get('/ai/git/log', { params: { project_id: pid } });
      setCommits(r.data.commits);
    } catch (e) {
      flash(e.response?.data?.error || 'Failed to load history', true);
    }
  }, []);

  const loadIndex = useCallback(async (pid) => {
    try {
      const r = await api.get('/ai/index/status', { params: { project_id: pid } });
      setIndex(r.data);
    } catch { setIndex(null); }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    api.get('/ai/models')
      .then(r => { setModels(r.data.models); setModel(r.data.default); })
      .catch(() => setError('Could not reach the model provider'));
  }, []);

  useEffect(() => { loadConversations(projectId); }, [projectId, loadConversations]);

  useEffect(() => {
    setCommits(null);
    if (projectId) { loadGit(projectId); loadIndex(projectId); }
    else { setGit(null); setIndex(null); }
  }, [projectId, loadGit, loadIndex]);

  // Standing instructions follow the selected scope: the project's own column,
  // or the site-wide general-chat setting when no project is selected.
  useEffect(() => {
    if (projectId) {
      setInstructions(projects.find(p => p.id === projectId)?.instructions || '');
    } else {
      api.get('/ai/instructions')
        .then(r => setInstructions(r.data.instructions || ''))
        .catch(() => setInstructions(''));
    }
  }, [projectId, projects]);

  useEffect(() => {
    if (convId) loadConversation(convId); else setConv(null);
  }, [convId, loadConversation]);

  // Reattach after a reload: resume polling an in-flight task, or surface one
  // that failed while the page was away (its error only lives in ai_tasks).
  useEffect(() => {
    const t = conv?.task;
    if (!convId || !t) { setTaskError(''); return; }
    if (t.status === 'queued' || t.status === 'running') {
      setBusy(true);
      pollTask(t.id, convId);
    } else if (t.status === 'failed') {
      const msgs = conv.messages || [];
      if (msgs.length && msgs[msgs.length - 1].role === 'user') {
        setTaskError(t.error || 'Task failed');
      }
    }
  }, [conv, convId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [conv]);

  // -------------------------------------------------------------------------
  // Projects
  // -------------------------------------------------------------------------
  const addProject = async () => {
    try {
      await api.post('/ai/projects/add', newProject);
      setNewProject(BLANK_PROJECT);
      setShowAddProject(false);
      await loadProjects();
      flash('Project added');
    } catch (e) { fail(e, 'Failed to add project'); }
  };

  const deleteProject = async (pid) => {
    if (!window.confirm('Delete this project and all its conversations?')) return;
    try {
      await api.post('/ai/projects/delete', { project_id: pid });
      if (projectId === pid) { setProjectId(null); setConvId(null); }
      await loadProjects();
      flash('Project deleted');
    } catch (e) { fail(e, 'Failed to delete project'); }
  };

  // -------------------------------------------------------------------------
  // Conversations
  // -------------------------------------------------------------------------
  const addConversation = async () => {
    try {
      const r = await api.post('/ai/conversations/add', {
        project_id: projectId,
        model,
        title: newTitle.trim() || new Date().toLocaleString(),
      });
      setNewTitle('');
      await loadConversations(projectId);
      setConvId(r.data.conversation.id);
    } catch (e) { fail(e, 'Failed to create conversation'); }
  };

  const renameConversation = async (c) => {
    const title = window.prompt('Name this conversation (empty clears the name):', c.title || '');
    if (title === null) return;  // cancelled
    try {
      await api.post('/ai/conversations/update', { conversation_id: c.id, title });
      await loadConversations(projectId);
      if (convId === c.id) await loadConversation(c.id);  // refresh the open chat header
    } catch (e) { fail(e, 'Failed to rename conversation'); }
  };

  const saveInstructions = async () => {
    try {
      if (projectId) {
        const r = await api.post('/ai/projects/update', { project_id: projectId, instructions });
        setProjects(ps => ps.map(p => (p.id === projectId ? r.data.project : p)));
      } else {
        await api.post('/ai/instructions', { instructions });
      }
      flash('Instructions saved - they apply from the next message');
    } catch (e) { fail(e, 'Failed to save instructions'); }
  };

  const deleteConversation = async (cid) => {
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await api.post('/ai/conversations/delete', { conversation_id: cid });
      if (convId === cid) setConvId(null);
      await loadConversations(projectId);
    } catch (e) { fail(e, 'Failed to delete conversation'); }
  };

  // -------------------------------------------------------------------------
  // Messaging
  // -------------------------------------------------------------------------
  const pollTask = (taskId, cid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get('/ai/tasks/status', { params: { task_id: taskId } });
        if (r.data.status === 'done' || r.data.status === 'failed') {
          clearInterval(pollRef.current);
          setBusy(false);
          if (r.data.status === 'failed') flash(r.data.error || 'Task failed', true);
          await loadConversation(cid);
          if (projectId) loadGit(projectId);   // the model may have edited files
        }
      } catch {
        clearInterval(pollRef.current);
        setBusy(false);
        flash('Lost contact with the task queue', true);
      }
    }, 1500);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !convId || busy) return;
    setInput('');
    setTaskError('');
    setBusy(true);
    try {
      const r = await api.post('/ai/conversations/message', { conversation_id: convId, content: text });
      await loadConversation(convId);
      pollTask(r.data.task_id, convId);
    } catch (e) {
      setBusy(false);
      fail(e, 'Failed to send message');
    }
  };

  const onKeyDown = (e) => {
    // Enter sends; Shift+Enter is a newline.
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // -------------------------------------------------------------------------
  // Git actions
  // -------------------------------------------------------------------------
  const viewDiff = async () => {
    try {
      const r = await api.get('/ai/git/diff', { params: { project_id: projectId } });
      setDiff(r.data);
    } catch (e) { fail(e, 'Failed to load diff'); }
  };

  const doCommit = async () => {
    const message = commitMsg.trim();
    if (!message) return flash('A commit message is required', true);
    try {
      const r = await api.post('/ai/git/commit', { project_id: projectId, message });
      setCommitMsg('');
      await loadGit(projectId);
      if (commits) await loadCommits(projectId);
      flash(r.data.message || 'Committed');
    } catch (e) { fail(e, 'Commit failed'); }
  };

  const doRestore = async () => {
    if (!window.confirm('Discard all uncommitted changes? This cannot be undone.')) return;
    const alsoNew = window.confirm('Also delete new untracked files? OK = yes, Cancel = keep them.');
    try {
      const r = await api.post('/ai/git/restore', { project_id: projectId, include_untracked: alsoNew });
      await loadGit(projectId);
      flash(r.data.message || 'Restored');
    } catch (e) { fail(e, 'Restore failed'); }
  };

  const rebuildIndex = async () => {
    try {
      const r = await api.post('/ai/index/build', { project_id: projectId });
      setBusy(true);
      flash('Indexing started');
      // Reuses the task poller; the index refreshes when the task finishes.
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const t = await api.get('/ai/tasks/status', { params: { task_id: r.data.task_id } });
          if (t.data.status === 'done' || t.data.status === 'failed') {
            clearInterval(pollRef.current);
            setBusy(false);
            if (t.data.status === 'failed') flash(t.data.error || 'Indexing failed', true);
            else flash(`Indexed ${t.data.result?.indexed ?? 0} file(s), ${t.data.result?.unchanged ?? 0} unchanged`);
            loadIndex(projectId);
          }
        } catch {
          clearInterval(pollRef.current);
          setBusy(false);
        }
      }, 2000);
    } catch (e) { fail(e, 'Failed to start indexing'); }
  };

  const doRollback = async (sha, subject) => {
    if (!window.confirm(`Roll back to "${subject}"?\n\nCommits after this point are discarded.`)) return;
    try {
      const r = await api.post('/ai/git/rollback', { project_id: projectId, sha });
      await loadGit(projectId);
      await loadCommits(projectId);
      flash(r.data.message || 'Rolled back');
    } catch (e) { fail(e, 'Rollback failed'); }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
      Loading…
    </div>
  );

  const project = projects.find(p => p.id === projectId);

  return (
    <div style={s.page}>
      <Navbar />

      <div style={s.content}>
        <div style={s.heading}>AI Plane</div>
        {error && <div style={s.err}>{error}</div>}
        {msg && <div style={s.ok}>{msg}</div>}

        <div style={s.columns}>
          {/* ---- Sidebar ---- */}
          <div style={s.side}>
            <div style={s.section}>
              <div style={s.sectionTitle}>Projects</div>

              <div
                style={{ ...s.listItem, ...(projectId === null ? s.listItemActive : {}) }}
                onClick={() => { setProjectId(null); setConvId(null); }}
              >
                General chat
                <div style={s.muted}>No project context</div>
              </div>

              {projects.map(p => (
                <div
                  key={p.id}
                  style={{ ...s.listItem, ...(projectId === p.id ? s.listItemActive : {}) }}
                  onClick={() => { setProjectId(p.id); setConvId(null); }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <span>{p.name}</span>
                    <span
                      style={{ ...s.muted, cursor: 'pointer' }}
                      onClick={e => { e.stopPropagation(); deleteProject(p.id); }}
                    >
                      ✕
                    </span>
                  </div>
                  <div style={{ ...s.muted, ...s.mono }}>{p.repo_path}</div>
                </div>
              ))}

              {!showAddProject ? (
                <div style={{ marginTop: '12px' }}>
                  <Btn onClick={() => setShowAddProject(true)} color="var(--accent-dim)" textColor="var(--accent)">
                    Add project
                  </Btn>
                </div>
              ) : (
                <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                  {[
                    ['name', 'Name'],
                    ['repo_path', 'Repo path (absolute)'],
                    ['service_name', 'systemd service (optional)'],
                    ['build_command', 'Build command (optional)'],
                    ['test_command', 'Test command (optional)'],
                    ['description', 'Description (optional)'],
                  ].map(([key, placeholder]) => (
                    <input
                      key={key}
                      style={{ ...s.input, width: '100%', marginBottom: '8px' }}
                      placeholder={placeholder}
                      value={newProject[key]}
                      onChange={e => setNewProject(f => ({ ...f, [key]: e.target.value }))}
                    />
                  ))}
                  <textarea
                    style={{ ...s.input, width: '100%', minHeight: '60px', resize: 'vertical', marginBottom: '8px', boxSizing: 'border-box' }}
                    placeholder="Instructions (optional) - sent with every message in this project"
                    value={newProject.instructions}
                    onChange={e => setNewProject(f => ({ ...f, instructions: e.target.value }))}
                  />
                  <div style={s.row}>
                    <Btn onClick={addProject}>Save</Btn>
                    <Btn
                      onClick={() => { setShowAddProject(false); setNewProject(BLANK_PROJECT); }}
                      color="var(--border)"
                      textColor="var(--text-muted)"
                    >
                      Cancel
                    </Btn>
                  </div>
                </div>
              )}
            </div>

            <div style={s.section}>
              <div style={s.sectionTitle}>Conversations</div>

              <input
                style={{ ...s.input, width: '100%', marginBottom: '8px', boxSizing: 'border-box' }}
                placeholder="Name for the new chat (optional)"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
              />
              <div style={{ ...s.row, marginBottom: '12px' }}>
                <select
                  style={{ ...s.input, flex: 1 }}
                  value={model}
                  onChange={e => setModel(e.target.value)}
                >
                  {models.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <Btn onClick={addConversation} disabled={!model}>New</Btn>
              </div>

              {conversations.length === 0 && <div style={s.muted}>None yet.</div>}
              {conversations.map(c => (
                <div
                  key={c.id}
                  style={{ ...s.listItem, ...(convId === c.id ? s.listItemActive : {}) }}
                  onClick={() => setConvId(c.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <span>{c.title || `Conversation ${c.id}`}</span>
                    <span style={{ display: 'flex', gap: '10px' }}>
                      <span
                        style={{ ...s.muted, cursor: 'pointer' }}
                        title="Rename"
                        onClick={e => { e.stopPropagation(); renameConversation(c); }}
                      >
                        ✎
                      </span>
                      <span
                        style={{ ...s.muted, cursor: 'pointer' }}
                        title="Delete"
                        onClick={e => { e.stopPropagation(); deleteConversation(c.id); }}
                      >
                        ✕
                      </span>
                    </span>
                  </div>
                  <div style={{ ...s.muted, ...s.mono }}>{c.model}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ---- Main ---- */}
          <div style={s.main}>
            <div style={s.section}>
              <div style={s.sectionTitle}>Instructions · {project ? project.name : 'General chat'}</div>
              <div style={{ ...s.muted, marginBottom: '10px' }}>
                Sent with every message in this {project ? 'project' : 'general chat'} - use it for
                standing guidance like conventions, tone, or things the assistant must always do or never do.
              </div>
              <textarea
                style={{ ...s.input, width: '100%', minHeight: '90px', resize: 'vertical', marginBottom: '10px', boxSizing: 'border-box' }}
                placeholder="e.g. Always run the test suite before committing. Never edit production config…"
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
              />
              <Btn onClick={saveInstructions}>Save instructions</Btn>
            </div>

            {project && (
              <div style={s.section}>
                <div style={s.sectionTitle}>Repository</div>
                {git?.error && <div style={s.err}>{git.error}</div>}
                {git && !git.error && (
                  <>
                    <div style={{ ...s.mono, marginBottom: '8px' }}>
                      {git.branch} · {git.commit?.short} · {git.clean ? 'clean' : `${git.files.length} changed`}
                    </div>
                    <div style={{ ...s.muted, marginBottom: '12px' }}>{git.commit?.subject}</div>

                    <div style={{ ...s.muted, marginBottom: '12px' }}>
                      Index: {index ? `${index.files} file(s)` : 'none'}
                      {index?.last_indexed ? ` · ${fmt(index.last_indexed)}` : ''}
                    </div>

                    {!git.clean && (
                      <div style={{ marginBottom: '12px' }}>
                        {git.files.map(f => (
                          <div key={f.path} style={s.mono}>
                            <span style={{ color: 'var(--accent)' }}>{f.code || '??'}</span> {f.path}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ ...s.row, marginBottom: '12px' }}>
                      <Btn onClick={() => loadGit(projectId)} color="var(--accent-dim)" textColor="var(--accent)">
                        Refresh
                      </Btn>
                      {!git.clean && <Btn onClick={viewDiff} color="var(--accent-dim)" textColor="var(--accent)">View diff</Btn>}
                      {diff && <Btn onClick={() => setDiff(null)} color="var(--border)" textColor="var(--text-muted)">Hide diff</Btn>}
                      {!git.clean && <Btn onClick={doRestore} color="var(--error-dim)" textColor="var(--error)">Restore</Btn>}
                      <Btn onClick={rebuildIndex} color="var(--accent-dim)" textColor="var(--accent)" disabled={busy}>
                        Rebuild index
                      </Btn>
                      <Btn
                        onClick={() => (commits ? setCommits(null) : loadCommits(projectId))}
                        color="var(--accent-dim)"
                        textColor="var(--accent)"
                      >
                        {commits ? 'Hide history' : 'History'}
                      </Btn>
                    </div>

                    {!git.clean && (
                      <div style={{ ...s.row, marginBottom: '12px' }}>
                        <input
                          style={{ ...s.input, flex: 1, minWidth: '180px' }}
                          placeholder="Commit message"
                          value={commitMsg}
                          onChange={e => setCommitMsg(e.target.value)}
                        />
                        <Btn onClick={doCommit} color="var(--success)">Commit</Btn>
                      </div>
                    )}

                    {diff && (
                      <div style={{ marginBottom: '12px' }}>
                        <pre style={s.pre}>{diff.diff || '(no changes to tracked files)'}</pre>
                        {diff.truncated && <div style={s.muted}>Diff truncated.</div>}
                      </div>
                    )}

                    {commits && (
                      <div>
                        {!git.clean && (
                          <div style={{ ...s.muted, marginBottom: '8px' }}>
                            Rollback is unavailable while there are uncommitted changes.
                          </div>
                        )}
                        {commits.map((c, i) => (
                          <div key={c.sha} style={s.commitRow}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '13px', wordBreak: 'break-word' }}>{c.subject}</div>
                              <div style={{ ...s.muted, ...s.mono }}>
                                {c.short} · {fmt(c.date)}{i === 0 ? ' · current' : ''}
                              </div>
                            </div>
                            {i > 0 && git.clean && (
                              <Btn onClick={() => doRollback(c.sha, c.subject)} color="var(--error-dim)" textColor="var(--error)">
                                Roll back
                              </Btn>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={s.section}>
              <div style={s.sectionTitle}>
                {conv?.conversation
                  ? `${conv.conversation.title || `Conversation ${conv.conversation.id}`} · ${conv.conversation.model}`
                  : 'Chat'}
              </div>

              {!convId && <div style={s.muted}>Select a conversation, or start a new one.</div>}

              {convId && conv && (
                <>
                  <div style={s.messages}>
                    {conv.messages.map(m => (
                      m.role === 'tool'
                        ? <ToolMessage key={m.id} m={m} />
                        : (
                          <div
                            key={m.id}
                            style={{
                              ...s.bubble,
                              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                              background: m.role === 'user' ? 'var(--accent-dim)' : 'var(--bg)',
                              border: '1px solid var(--border)',
                            }}
                          >
                            {m.content}
                            <div style={{ ...s.muted, marginTop: '6px' }}>
                              {fmt(m.created_at)}
                              {m.tokens_in != null && ` · ${m.tokens_in} in / ${m.tokens_out} out`}
                              {m.role === 'assistant' && (
                                <>
                                  {' · '}
                                  <CopyBtn text={m.content} />
                                </>
                              )}
                            </div>
                          </div>
                        )
                    ))}
                    {busy && <div style={s.muted}>Working…</div>}
                    {taskError && (
                      <div
                        style={{
                          ...s.bubble,
                          alignSelf: 'flex-start',
                          background: 'var(--error-dim)',
                          border: '1px solid var(--error)',
                          color: 'var(--error)',
                          width: 'fit-content',
                        }}
                      >
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                          <span style={{ whiteSpace: 'pre-wrap' }}>{taskError}</span>
                          <span
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                            title="Dismiss"
                            onClick={() => setTaskError('')}
                          >
                            ✕
                          </span>
                        </div>
                      </div>
                    )}
                    <div ref={endRef} />
                  </div>

                  <div style={s.row}>
                    <textarea
                      style={{ ...s.input, flex: 1, minHeight: '58px', resize: 'vertical', fontFamily: "'Space Grotesk', sans-serif" }}
                      placeholder="Message… (Enter to send, Shift+Enter for a new line)"
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={onKeyDown}
                    />
                    <Btn onClick={send} disabled={busy || !input.trim()}>Send</Btn>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
